// Single door for hierarchy moves. The report stays out-of-band so a no-op still reports.
import { buildLayout } from "../buildLayout";
import { moveSibling, reorderSpineLevel } from "../studioTransactions";
import {
  GeneratedLayout,
  LayoutProjection,
  StudioDraft,
  StudioItem,
  StudioRunGeometry,
} from "../studioTypes";
import { blockingScoreOf, fixVerticalBlocking } from "../verticalBlockingFix";
import { remeasureDraft } from "./annotateFromLayout";
import { preserveUntouchedScopes } from "./levelMoveLocality";
import { HierarchyMove, MoveClass, classifyMove } from "./classifyMove";
import { DEFAULT_HIERARCHY_MOVE_RULES, HierarchyMoveRules } from "./hierarchyMoveRules";
import { MoveAudit, auditMove, shelfExactRate } from "./moveAcceptance";
import { rehomeBlock, resolveScope } from "./rehomeBlock";
import { branchSkuIdsOf, swapLevelsUnderScope } from "./scopedLevelSwap";
import { isBandedScope, rebandScope } from "./rebandScope";
import { applyHierarchySwap } from "../cdtLayout/hierarchySwap";
import { closeFillGap, fillByRunOf, fillShareOf } from "../cdtLayout/closeFillGap";
import type { AestheticsRules } from "../aestheticsRules";

/**
 * Which mechanism executed the move. "slide" = whole-block move in place (vertical
 * values); "reband" = tiers re-stacked in place (banded values); "remeasure" = the
 * displayed plan survived and the contract is re-measured under the new spine; "rebuild"
 * = the spec rebuild or the loud structural fallback, never a sequence move's default;
 * "branch-rebuild" = one branch re-lays, everything outside it replays in place.
 */
export type MoveMechanism = "slide" | "reband" | "remeasure" | "rebuild" | "branch-rebuild";

export interface MoveConcession {
  kind:
    | "fresh-fallback"
    | "drift"
    | "ladder"
    | "stranded"
    | "not-banded"
    | "composite-drop"
    | "audit"
    | "shed"
    | "redistributed"
    | "dropped";
  detail: string;
}

/** Facing redistribution, named: where the committed contract cannot carry a transform's
 * exact geometry the generator re-deals facings, invisibly to the packer's shed census. */
export const redistributionConcessionOf = (
  before: GeneratedLayout,
  after: GeneratedLayout,
  items: readonly StudioItem[],
): MoveConcession[] => {
  const totals = (layout: GeneratedLayout): Map<string, number> => {
    const out = new Map<string, number>();
    for (const placement of Object.values(layout.placements)) {
      out.set(
        placement.skuId,
        placement.segments.reduce((sum, segment) => sum + segment.hFacings, 0),
      );
    }
    return out;
  };
  const a = totals(before);
  const b = totals(after);
  const nameOf = new Map(items.map((item) => [item.skuId, item.name || item.skuId]));
  const moved: { skuId: string; from: number; to: number }[] = [];
  for (const [skuId, from] of a) {
    const to = b.get(skuId) ?? 0;
    if (to !== from) moved.push({ skuId, from, to });
  }
  if (moved.length === 0) return [];
  const net = moved.reduce((sum, entry) => sum + (entry.to - entry.from), 0);
  const worst = [...moved]
    .sort((x, y) => Math.abs(y.to - y.from) - Math.abs(x.to - x.from))
    .slice(0, 3)
    .map((entry) => `${nameOf.get(entry.skuId) ?? entry.skuId} ${entry.from}→${entry.to}`);
  return [
    {
      kind: "redistributed",
      detail:
        `${moved.length} product(s) came back with a different facing count ` +
        `(net ${net >= 0 ? "+" : ""}${net}); the committed contract could not carry the ` +
        `move's exact geometry, so the generator re-dealt them — biggest: ${worst.join(", ")}`,
    },
  ];
};

/** The packer's shed record as a concession, BASELINE-SUBTRACTED per SKU: the base
 * regeneration carries standing sheds, and a move answers only for the ones it changed. */
export const shedConcessionOf = (
  after: { shedFacings?: readonly { skuId: string; requested: number; placed: number }[] },
  before: { shedFacings?: readonly { skuId: string; requested: number; placed: number }[] },
): MoveConcession[] => {
  const totalsOf = (layout: typeof after): Map<string, number> => {
    const totals = new Map<string, number>();
    for (const shed of layout.shedFacings ?? []) {
      totals.set(shed.skuId, (totals.get(shed.skuId) ?? 0) + (shed.requested - shed.placed));
    }
    return totals;
  };
  const baseline = totalsOf(before);
  let facings = 0;
  const skus = new Set<string>();
  for (const [skuId, amount] of totalsOf(after)) {
    const delta = amount - (baseline.get(skuId) ?? 0);
    if (delta > 0) {
      facings += delta;
      skus.add(skuId);
    }
  }
  if (facings === 0) return [];
  return [
    {
      kind: "shed",
      detail: `${facings} facing(s) newly shed across ${skus.size} product(s) to keep every product whole in its block`,
    },
  ];
};

export interface MoveReport {
  moveClass: MoveClass;
  /** True when the plan was preserved surgically or by re-measure; false when regenerated. */
  surgical: boolean;
  /** False = no-op; the returned draft is then the input draft by identity. */
  committed: boolean;
  audit: MoveAudit | null;
  /** Fixed-draft scoring, each side against its own declared draft. */
  aesthetics: { before: number; after: number; contractChanged: boolean } | null;
  /** Equal by construction while no move may touch the assortment. */
  expectedSales: { before: number; after: number } | null;
  /** Summed from the layouts: the shed census undercounts, since a facing that never got a slot to request from is gone but never "shed". */
  facings: { before: number; after: number; delta: number } | null;
  fill: {
    before: number;
    after: number;
    /** Worst run first; overflow kept separate, never laundered into utilization. */
    worstRun: { run: number; before: number; after: number; overflowCm: number } | null;
  } | null;
  mechanism: MoveMechanism | null;
  /** Measured on the artifact that ships; the mechanism copy is built from these, so it can only say what the panel's concessions say. */
  churn: {
    movedProducts: number;
    totalProducts: number;
    facingsRedealt: number;
  } | null;
  concessions: readonly MoveConcession[];
}

/** Build a value move from the sibling-drag gesture. `destIndex` is resolved against the
 * same order `moveSibling` reads, and the move carries the GENERATOR's scope key when the
 * tree supplies one — a tree-styled key can be dead to the generator, freezing the readout.
 * TODO(scope-keys): third site of the dual-key resolution; centralize into one helper. */
export const siblingMoveOf = (
  draft: StudioDraft,
  node: {
    scopeKey: string;
    attr: string;
    value: string;
    orderScopeKey?: string;
    siblings?: readonly string[];
  },
  offset: number,
): HierarchyMove => {
  const scopeKey = node.orderScopeKey ?? node.scopeKey;
  /* THE SIBLINGS ARE THE RULER: an offset expressed against the values on screen must be
     counted in those same values, never in plan-wide `valueOrder[attr]`. */
  const order =
    node.siblings ??
    draft.scopedValueOrder[scopeKey] ??
    draft.scopedValueOrder[node.scopeKey] ??
    draft.valueOrder[node.attr] ??
    [];
  return {
    kind: "value",
    scopeKey,
    attr: node.attr,
    value: node.value,
    destIndex: order.indexOf(node.value) + offset,
    ...(node.siblings ? { siblings: node.siblings } : {}),
  };
};

interface MoveContext {
  items: readonly StudioItem[];
  runs: readonly StudioRunGeometry[];
  rules: HierarchyMoveRules;
  aestheticsRules?: Partial<AestheticsRules>;
  verticalBlockShare?: number;
}

const reportOf = (
  moveClass: MoveClass,
  partial: Partial<Omit<MoveReport, "moveClass">> = {},
): MoveReport => ({
  moveClass,
  surgical: false,
  committed: true,
  audit: null,
  aesthetics: null,
  expectedSales: null,
  facings: null,
  fill: null,
  mechanism: null,
  churn: null,
  concessions: [],
  ...partial,
});

/** Facings on a plan: every placed segment's own count, summed. */
export const facingsOnPlan = (layout: GeneratedLayout): number => {
  let total = 0;
  for (const placement of Object.values(layout.placements)) {
    for (const segment of placement.segments) total += segment.hFacings;
  }
  return total;
};

/** before/after/delta, for the report and the budget gate. */
export const facingsPairOf = (
  before: GeneratedLayout,
  after: GeneratedLayout,
): { before: number; after: number; delta: number } => {
  const from = facingsOnPlan(before);
  const to = facingsOnPlan(after);
  return { before: from, after: to, delta: to - from };
};

/** Budget gate thresholds: share of facings a move may remove, and percentage POINTS of
 * fill it may drop (a move can keep every facing and still empty the shelf). Gains never ask. */
export const FACINGS_LOSS_GATE = 0.02;
export const FILL_LOSS_GATE_PTS = 2;

export interface BudgetQuestion {
  /** Which cost crossed its threshold; facings loss outranks fill. */
  kind: "facings" | "fill";
  facingsLost: number;
  /** Share of the plan's facings removed, one decimal, in percent. */
  pct: string;
  /** Metres of shelf the fill drop stops covering, one decimal. */
  metres: string;
  fill: { before: number; after: number } | null;
}

/** One predicate for every gated commit site, so the budgets cannot drift between surfaces. */
export const budgetQuestionOf = (
  report: MoveReport,
  shelfMetres: number,
): BudgetQuestion | null => {
  if (!report.committed) return null;
  const facings = report.facings;
  const fill = report.fill;
  const lost = facings ? -facings.delta : 0;
  const fillDrop = fill ? fill.before - fill.after : 0;
  const metres = ((Math.max(fillDrop, 0) / 100) * shelfMetres).toFixed(1);
  const pct = facings && facings.before > 0 ? ((lost / facings.before) * 100).toFixed(1) : "0.0";
  if (facings && lost > facings.before * FACINGS_LOSS_GATE) {
    return { kind: "facings", facingsLost: lost, pct, metres, fill };
  }
  if (fill && fillDrop > FILL_LOSS_GATE_PTS) {
    return { kind: "fill", facingsLost: Math.max(lost, 0), pct, metres, fill };
  }
  return null;
};

export const churnOf = (
  before: GeneratedLayout,
  after: GeneratedLayout,
  project: LayoutProjection = (layout) => layout,
): NonNullable<MoveReport["churn"]> => {
  const sigOf = (layout: GeneratedLayout) => {
    const pos = new Map<string, string>();
    const fac = new Map<string, number>();
    for (const placement of Object.values(layout.placements)) {
      const segments = [...placement.segments].sort(
        (a, b) => a.run - b.run || a.shelf - b.shelf || a.xCm - b.xCm,
      );
      pos.set(
        placement.skuId,
        segments.map((s) => `${s.run}/${s.shelf}/${s.xCm.toFixed(1)}`).join(","),
      );
      fac.set(
        placement.skuId,
        segments.reduce((sum, s) => sum + s.hFacings, 0),
      );
    }
    return { pos, fac };
  };
  // POSITION on the drawn basis (contract xCm is an anchor); FACINGS on the layout's own.
  const drawnA = sigOf(project(before));
  const drawnB = sigOf(project(after));
  const a = sigOf(before);
  const b = sigOf(after);
  let movedProducts = 0;
  let facingsRedealt = 0;
  for (const [skuId, sig] of drawnA.pos) {
    if (drawnB.pos.get(skuId) !== sig) movedProducts += 1;
  }
  for (const [skuId, facings] of a.fac) {
    if (b.fac.get(skuId) !== facings) facingsRedealt += 1;
  }
  return { movedProducts, totalProducts: a.pos.size, facingsRedealt };
};

/** Fill per side, in percent: aggregate plus the committed plan's worst run. */
const fillPairOf = (
  before: { layout: Parameters<typeof fillShareOf>[0] },
  after: { layout: Parameters<typeof fillShareOf>[0] },
  items: readonly StudioItem[],
  runs: readonly StudioRunGeometry[],
): NonNullable<MoveReport["fill"]> => {
  const beforeByRun = fillByRunOf(before.layout, items, runs);
  const afterByRun = fillByRunOf(after.layout, items, runs);
  const worst = [...afterByRun].sort((a, b) => a.share - b.share)[0];
  return {
    before: +(100 * fillShareOf(before.layout, items, runs)).toFixed(1),
    after: +(100 * fillShareOf(after.layout, items, runs)).toFixed(1),
    worstRun: worst
      ? {
          run: worst.run,
          before: +(
            100 * (beforeByRun.find((entry) => entry.run === worst.run)?.share ?? 0)
          ).toFixed(1),
          after: +(100 * worst.share).toFixed(1),
          overflowCm: +worst.overflowCm.toFixed(1),
        }
      : null,
  };
};

/** Floor for check 11, scaled to the plan (never above total). */
const floorExactOf = (total: number, rules: HierarchyMoveRules): number =>
  Math.min(total, Math.floor(total * rules.roundTripFloorShare));

const applyLevelMove = (
  context: MoveContext,
  draft: StudioDraft,
  move: Extract<HierarchyMove, { kind: "level" }>,
  moveClass: MoveClass,
): { draft: StudioDraft; report: MoveReport } => {
  const { items, runs, rules, aestheticsRules, verticalBlockShare } = context;
  const wiped = reorderSpineLevel(draft, move.from, move.to);
  if (wiped === draft) {
    return { draft, report: reportOf(moveClass, { committed: false }) };
  }
  const displayed = buildLayout(items, draft, runs);

  // The plan the move intends: the displayed one, or a fresh build under the moved spine.
  const remeasureOnly = moveClass === "remeasure-only";
  const intended = remeasureOnly ? displayed : buildLayout(items, wiped, runs);

  const concessions: MoveConcession[] = [];
  let next = wiped;
  let surgical = false;
  try {
    const candidate = remeasureDraft(items, intended, runs, wiped);
    const reproduced = buildLayout(items, candidate, runs);
    const rate = shelfExactRate(intended, reproduced);
    // Faithful within a measured FLOOR, not exact: as-built mode is not a perfect fixpoint,
    // so demanding exactness would drop a near-faithful candidate to fresh. Drift is reported.
    const floor = floorExactOf(rate.total, rules);
    if (rate.exact >= floor) {
      next = candidate;
      surgical = remeasureOnly;
      if (remeasureOnly && rate.exact < rate.total) {
        concessions.push({
          kind: "drift",
          detail:
            `the plan is preserved for ${rate.exact}/${rate.total} products; ` +
            `${rate.total - rate.exact} settle on a neighbouring shelf`,
        });
      }
    } else {
      concessions.push({
        kind: "fresh-fallback",
        detail:
          `re-measurement reproduced ${rate.exact}/${rate.total} shelf-exact ` +
          `(needed ${floor}); measured structure discarded, the plan regenerates fresh`,
      });
    }
  } catch (error) {
    concessions.push({
      kind: "fresh-fallback",
      detail: `re-measurement refused: ${error instanceof Error ? error.message : String(error)}`,
    });
  }

  // LOCALITY on every rung: re-measurement is plan-wide and the fresh fallback starts
  // wiped, so carry every contract entry above the moved pair forward untouched.
  next = preserveUntouchedScopes(draft, next, move.from, move.to);

  const after = buildLayout(items, next, runs);
  concessions.push(...shedConcessionOf(after, displayed));
  concessions.push(...redistributionConcessionOf(displayed, after, items));
  // The level path scores like the value path: one report payload for every move.
  const aesthetics = {
    before: blockingScoreOf(items, displayed, runs, draft, aestheticsRules, verticalBlockShare),
    after: blockingScoreOf(items, after, runs, next, aestheticsRules, verticalBlockShare),
    contractChanged: true,
  };
  const expectedSales = {
    before: expectedSalesOf(displayed, items),
    after: expectedSalesOf(after, items),
  };
  const audit = auditMove({
    items,
    runs,
    before: { layout: displayed, draft },
    after: { layout: after, draft: next },
    roundTrip: { reproduced: intended, floorExact: floorExactOf(items.length, rules) },
  });
  // Fresh is the last rung: its audit failures are reported, never blocking.
  return {
    draft: next,
    report: reportOf(moveClass, {
      surgical,
      audit,
      concessions,
      aesthetics,
      expectedSales,
      facings: facingsPairOf(displayed, after),
      fill: fillPairOf({ layout: displayed }, { layout: after }, items, runs),
      // Re-measured under the new spine: nothing is re-derived and nothing slides.
      mechanism: surgical ? "remeasure" : "rebuild",
      churn: churnOf(displayed, after),
    }),
  };
};

/**
 * The ladder's branch-local lift: the pog must follow the tree. After the branch's level
 * order is permuted through `scopedLevelOrder`, the branch's OWN shelf hints are dropped
 * so the generator re-lays exactly that branch while every SKU outside it replays in
 * place. A multi-rung drag is a run of adjacent swaps folded over ONE draft, committed
 * once. Indices are BRANCH indices (positions in levelsForScope).
 */
const applyScopedLevelLift = (
  context: MoveContext,
  draft: StudioDraft,
  move: Extract<HierarchyMove, { kind: "scoped-level" }>,
  moveClass: MoveClass,
): { draft: StudioDraft; report: MoveReport } => {
  const { items, runs, rules, aestheticsRules, verticalBlockShare } = context;
  let reordered = draft;
  for (let upper = move.from - 1; upper >= move.to; upper -= 1) {
    reordered = swapLevelsUnderScope(items, reordered, move.scopeNodeId, upper);
  }
  if (reordered === draft) {
    return { draft, report: reportOf(moveClass, { committed: false }) };
  }
  // Membership is read off the PRE-swap draft, which cannot be confused by the new order.
  const branchSkus = branchSkuIdsOf(items, draft, move.scopeNodeId);
  const shelfHints = Object.fromEntries(
    Object.entries(reordered.shelfHints).filter(([skuId]) => !branchSkus.has(skuId)),
  );
  const next: StudioDraft = { ...reordered, shelfHints };

  const displayed = buildLayout(items, draft, runs);
  const after = buildLayout(items, next, runs);
  const concessions: MoveConcession[] = [];
  concessions.push(...shedConcessionOf(after, displayed));
  concessions.push(...redistributionConcessionOf(displayed, after, items));
  const audit = auditMove({
    items,
    runs,
    before: { layout: displayed, draft },
    after: { layout: after, draft: next },
    roundTrip: { reproduced: after, floorExact: floorExactOf(items.length, rules) },
  });
  if (!audit.pass) {
    concessions.push({
      kind: "audit",
      detail:
        `the re-laid branch carries ${audit.failures.length} audit finding(s) ` +
        `(${[...new Set(audit.failures.map((failure) => `#${failure.check}`))].join(" ")}) — ` +
        "the un-moved plan is one undo away",
    });
  }
  const aesthetics = {
    before: blockingScoreOf(items, displayed, runs, draft, aestheticsRules, verticalBlockShare),
    after: blockingScoreOf(items, after, runs, next, aestheticsRules, verticalBlockShare),
    contractChanged: true,
  };
  return {
    draft: next,
    report: reportOf(moveClass, {
      // The branch is freshly generated, not preserved — say so.
      surgical: false,
      audit,
      concessions,
      aesthetics,
      expectedSales: {
        before: expectedSalesOf(displayed, items),
        after: expectedSalesOf(after, items),
      },
      facings: facingsPairOf(displayed, after),
      fill: fillPairOf({ layout: displayed }, { layout: after }, items, runs),
      mechanism: "branch-rebuild",
      churn: churnOf(displayed, after),
    }),
  };
};

const applyValueMove = (
  draft: StudioDraft,
  move: Extract<HierarchyMove, { kind: "value" }>,
  moveClass: MoveClass,
): { draft: StudioDraft; report: MoveReport } => {
  // Must be the same ruler `siblingMoveOf` measured `destIndex` with.
  const order =
    move.siblings ?? draft.scopedValueOrder[move.scopeKey] ?? draft.valueOrder[move.attr] ?? [];
  const from = order.indexOf(move.value);
  const next = moveSibling(
    draft,
    move.scopeKey,
    move.attr,
    move.value,
    move.destIndex - from,
    move.siblings,
  );
  return {
    draft: next,
    report: reportOf(moveClass, { committed: next !== draft }),
  };
};

/** Checks whose failure the surgical rung may never ship: assortment (3), overlap (5),
 * facing floor (7). Generator replay preserves all three by construction, so a failure
 * there is always surgical-only. Softer failures commit NAMED as concessions. */
const SURGICAL_HARD_VETO: ReadonlySet<number> = new Set([3, 5, 7]);

/** Expected sales a layout keeps: the sum over placed SKUs, counted once each. Moves
 * never touch range, so before === after is the assertion. */
const expectedSalesOf = (layout: GeneratedLayout, items: readonly StudioItem[]): number => {
  const placed = new Set<string>();
  for (const placement of Object.values(layout.placements)) {
    if (placement.segments.some((segment) => segment.hFacings >= 1)) placed.add(placement.skuId);
  }
  let total = 0;
  for (const item of items) if (placed.has(item.skuId)) total += item.salesAmount ?? 0;
  return +total.toFixed(2);
};

/** The surgical rehome, full ladder: rehome → seam pass → re-measure → regenerate → audit
 * → score. A hard-audit failure falls back to generator replay of the same intent, openly. */
const applySurgicalValueMove = (
  context: MoveContext,
  draft: StudioDraft,
  move: Extract<HierarchyMove, { kind: "value" }>,
  moveClass: MoveClass,
): { draft: StudioDraft; report: MoveReport } => {
  const { items, runs, rules, aestheticsRules, verticalBlockShare } = context;
  const displayed = buildLayout(items, draft, runs);
  const rehome = rehomeBlock(items, runs, draft, displayed, move, rules);
  if (!rehome) {
    const legacy = applyValueMove(draft, move, moveClass);
    return {
      draft: legacy.draft,
      report: {
        ...legacy.report,
        mechanism: "rebuild",
        concessions: [
          {
            kind: "fresh-fallback",
            detail:
              "REBUILD (structural): the moved scope could not be resolved; " +
              "generator replay executed the move",
          },
        ],
      },
    };
  }
  const orderUnchanged =
    rehome.declaredOrder.length === rehome.previousOrder.length &&
    rehome.declaredOrder.every((key, index) => rehome.previousOrder[index] === key);
  // THE MOVER MUST ACTUALLY MOVE: leaving the selected value put while re-ordering its
  // neighbours is worse than doing nothing. Judged on the DECLARED contract, not the
  // rehome's measured reading order — the readout is the promise the user can check.
  const declaredBefore =
    draft.scopedValueOrder[rehome.scopeGeneratorKey] ?? draft.valueOrder[move.attr] ?? [];
  const moverBefore = declaredBefore.indexOf(move.value);
  const moverAfter = rehome.declaredOrder.indexOf(move.value);
  const moverStayed = moverBefore >= 0 && moverAfter >= 0 && moverBefore === moverAfter;
  if (orderUnchanged || moverStayed) {
    return { draft, report: reportOf(moveClass, { committed: false }) };
  }

  // Seam pass: its overflow relief is exempt from the range floor, so a squared result
  // that spent a SKU is refused here. The facings it re-deals are named, never silent.
  let candidate = rehome.layout;
  try {
    const squared = fixVerticalBlocking(items, candidate, runs, draft, {
      preserveRange: true,
      onlyIfBetter: true,
      rules: aestheticsRules,
      verticalBlockShare,
    });
    const facingsNeutral = churnOf(candidate, squared.layout).facingsRedealt === 0;
    const allowed =
      rules.seamPass === "on" || (rules.seamPass === "facings-neutral" && facingsNeutral);
    if (!squared.refusedSparse && squared.unranged.length === 0 && allowed) {
      candidate = squared.layout;
    }
  } catch {
    // The pass could not run; the un-squared candidate stands and the audit judges it.
  }

  const concessions: MoveConcession[] = rehome.concessions.map((concession) => ({
    kind: concession.kind,
    detail: concession.detail,
  }));
  const declared = {
    previous: rehome.previousOrder,
    next: rehome.declaredOrder,
    travelling: rehome.travelling,
    blockKeyOf: rehome.blockKeyOfItem,
    outsideRankOf: rehome.outsideRankOf,
  };
  const scoreBefore = blockingScoreOf(
    items,
    displayed,
    runs,
    draft,
    aestheticsRules,
    verticalBlockShare,
  );
  const salesBefore = expectedSalesOf(displayed, items);

  // Both rungs are judged against EACH OTHER: audit failures the fallback shares are the
  // generator's cost of executing the intent at all, not the surgery's.
  interface Rung {
    draft: StudioDraft;
    audit: MoveAudit;
    score: number;
  }
  let surgicalRung: Rung | null = null;
  try {
    const remeasured = remeasureDraft(items, candidate, runs, draft);
    // The committed contract carries the DECLARED order, not the re-measured one: one
    // click must be one step in the sibling readout and in the next move's base.
    const carried: StudioDraft = {
      ...remeasured,
      scopedValueOrder: {
        ...remeasured.scopedValueOrder,
        [rehome.scopeGeneratorKey]: rehome.declaredOrder,
      },
    };
    // NO FILL TRADING ON AN IN-PLACE PATH: `closeFillGap` buys fill by trading facings
    // across the whole plan, which a slide must not do. It belongs to the rebuild paths.
    const measured = carried;
    const reproduced = buildLayout(items, measured, runs);
    surgicalRung = {
      draft: measured,
      audit: auditMove({
        items,
        runs,
        before: { layout: displayed, draft },
        after: { layout: reproduced, draft: measured },
        declared,
        roundTrip: { reproduced: candidate, floorExact: floorExactOf(items.length, rules) },
        // A slide moves intact blocks, so fill must hold the before-state within tolerance.
        fill: { floorShare: rules.fillFloorShare, dropTolerancePts: rules.fillDropTolerancePts },
      }),
      score: blockingScoreOf(
        items,
        reproduced,
        runs,
        measured,
        aestheticsRules,
        verticalBlockShare,
      ),
    };
  } catch {
    surgicalRung = null;
  }
  // THE SLIDE IS THE DEFAULT RUNG; replay runs only on a hard fail — unmeasurable, or
  // checks 3/5/7, which a bounded slide cannot cause, so their presence is structural.
  const hardFailed =
    surgicalRung === null ||
    surgicalRung.audit.failures.some((failure) => SURGICAL_HARD_VETO.has(failure.check));
  let chosen: Rung;
  let surgical: boolean;
  if (!hardFailed && surgicalRung !== null) {
    chosen = surgicalRung;
    surgical = true;
  } else {
    const fallbackDraft: StudioDraft = {
      ...draft,
      scopedValueOrder: {
        ...draft.scopedValueOrder,
        [rehome.scopeGeneratorKey]: rehome.declaredOrder,
      },
    };
    const fallbackLayout = buildLayout(items, fallbackDraft, runs);
    chosen = {
      draft: fallbackDraft,
      audit: auditMove({
        items,
        runs,
        before: { layout: displayed, draft },
        after: { layout: fallbackLayout, draft: fallbackDraft },
        declared,
      }),
      score: blockingScoreOf(
        items,
        fallbackLayout,
        runs,
        fallbackDraft,
        aestheticsRules,
        verticalBlockShare,
      ),
    };
    surgical = false;
    concessions.push({
      kind: "fresh-fallback",
      detail:
        surgicalRung === null
          ? "REBUILD (structural): the in-place slide could not be measured; generator replay executed the move"
          : "REBUILD (structural): the slide's committed candidate failed hard check(s) " +
            `${[
              ...new Set(
                surgicalRung.audit.failures
                  .filter((failure) => SURGICAL_HARD_VETO.has(failure.check))
                  .map((failure) => `#${failure.check}`),
              ),
            ].join(" ")}; generator replay executed the move`,
    });
  }
  const next: StudioDraft = chosen.draft;
  const audit: MoveAudit = chosen.audit;
  const scoreAfter = chosen.score;
  if (!audit.pass) {
    concessions.push({
      kind: "audit",
      detail:
        `the committed plan carries ${audit.failures.length} audit finding(s) ` +
        `(${[...new Set(audit.failures.map((failure) => `#${failure.check}`))].join(" ")}) — ` +
        "the generator's cost of executing this move; the un-moved plan is one undo away",
    });
  }
  if (!Number.isNaN(scoreBefore) && !Number.isNaN(scoreAfter) && scoreAfter < scoreBefore) {
    concessions.push({
      kind: "composite-drop",
      detail:
        `the move costs aesthetics ${scoreBefore} to ${scoreAfter}; ` +
        "this is the best of the measured candidates — undo rejects it",
    });
  }

  const afterLayout = buildLayout(items, next, runs);
  concessions.push(...shedConcessionOf(afterLayout, displayed));
  concessions.push(...redistributionConcessionOf(displayed, afterLayout, items));
  return {
    draft: next,
    report: reportOf(moveClass, {
      surgical,
      audit,
      concessions,
      aesthetics: { before: scoreBefore, after: scoreAfter, contractChanged: true },
      expectedSales: { before: salesBefore, after: expectedSalesOf(afterLayout, items) },
      facings: facingsPairOf(displayed, afterLayout),
      fill: fillPairOf({ layout: displayed }, { layout: afterLayout }, items, runs),
      mechanism: surgical ? "slide" : "rebuild",
      churn: churnOf(displayed, afterLayout),
    }),
  };
};

/**
 * The re-band rung — resequencing a level whose values live as horizontal BANDS. Products
 * keep their facings and window; the tiers re-stack in place. Structural impossibility
 * falls back to generator replay, reported loudly as a rebuild. */
const applyRebandMove = (
  context: MoveContext,
  draft: StudioDraft,
  move: Extract<HierarchyMove, { kind: "value" }>,
  moveClass: MoveClass,
  displayed: GeneratedLayout,
): { draft: StudioDraft; report: MoveReport } => {
  const { items, runs, rules, aestheticsRules, verticalBlockShare } = context;
  const reband = rebandScope(items, runs, draft, displayed, move, rules);
  if (!reband) {
    const legacy = applyValueMove(draft, move, moveClass);
    return {
      draft: legacy.draft,
      report: {
        ...legacy.report,
        mechanism: "rebuild",
        concessions: [
          {
            kind: "fresh-fallback",
            detail:
              "REBUILD (structural): the banded scope could not be re-tiered in place; " +
              "generator replay executed the move",
          },
        ],
      },
    };
  }
  const orderUnchanged =
    reband.declaredOrder.length === reband.previousOrder.length &&
    reband.declaredOrder.every((key, index) => reband.previousOrder[index] === key);
  if (orderUnchanged) {
    return { draft, report: reportOf(moveClass, { committed: false, mechanism: "reband" }) };
  }

  const concessions: MoveConcession[] = [];
  const scoreBefore = blockingScoreOf(
    items,
    displayed,
    runs,
    draft,
    aestheticsRules,
    verticalBlockShare,
  );
  const salesBefore = expectedSalesOf(displayed, items);
  const remeasured = remeasureDraft(items, reband.layout, runs, draft);
  const carried: StudioDraft = {
    ...remeasured,
    scopedValueOrder: {
      ...remeasured.scopedValueOrder,
      [reband.scopeGeneratorKey]: reband.declaredOrder,
    },
  };
  // No fill trading on an in-place path — see the slide path's note.
  const next: StudioDraft = carried;
  const after = buildLayout(items, next, runs);
  const audit = auditMove({
    items,
    runs,
    before: { layout: displayed, draft },
    after: { layout: after, draft: next },
    roundTrip: { reproduced: reband.layout, floorExact: floorExactOf(items.length, rules) },
    // A resequence buys nothing with fill, so fill must hold the before-state.
    fill: { floorShare: rules.fillFloorShare, dropTolerancePts: rules.fillDropTolerancePts },
  });
  if (!audit.pass) {
    concessions.push({
      kind: "audit",
      detail:
        `the re-banded plan carries ${audit.failures.length} audit finding(s) ` +
        `(${[...new Set(audit.failures.map((failure) => `#${failure.check}`))].join(" ")}) — ` +
        "the un-moved plan is one undo away",
    });
  }
  const aesthetics = {
    before: scoreBefore,
    after: blockingScoreOf(items, after, runs, next, aestheticsRules, verticalBlockShare),
    contractChanged: true,
  };
  if (
    !Number.isNaN(aesthetics.before) &&
    !Number.isNaN(aesthetics.after) &&
    aesthetics.after < aesthetics.before
  ) {
    concessions.push({
      kind: "composite-drop",
      detail: `the move costs aesthetics ${aesthetics.before} to ${aesthetics.after}; undo rejects it`,
    });
  }
  concessions.push(...shedConcessionOf(after, displayed));
  concessions.push(...redistributionConcessionOf(displayed, after, items));
  return {
    draft: next,
    report: reportOf(moveClass, {
      surgical: true,
      audit,
      concessions,
      aesthetics,
      expectedSales: { before: salesBefore, after: expectedSalesOf(after, items) },
      fill: fillPairOf({ layout: displayed }, { layout: after }, items, runs),
      mechanism: "reband",
      churn: churnOf(displayed, after),
    }),
  };
};

/** The two hierarchy swaps (merge into a neighbour's window / stand up as a vertical).
 * Orientation changes rebuild through the spec's own layout algorithm — no incremental
 * version exists — and the result carries back through re-measurement. Rebuild is this
 * class's only rung, so audit findings are committed NAMED. */
const applySwapMove = (
  context: MoveContext,
  draft: StudioDraft,
  move: Extract<HierarchyMove, { kind: "merge" | "unmerge" }>,
  moveClass: MoveClass,
): { draft: StudioDraft; report: MoveReport } => {
  const { items, runs, rules, aestheticsRules, verticalBlockShare } = context;
  const displayed = buildLayout(items, draft, runs);
  const swap = applyHierarchySwap(
    items,
    runs,
    draft,
    {
      attr: move.attr,
      value: move.value,
      orientation: move.kind === "merge" ? "pooled" : "vertical",
    },
    { minFacingsPerShelf: rules.minFacingsPerShelf },
  );
  const concessions: MoveConcession[] = [];
  if (swap.report.dropped.length > 0) {
    // The studio never delists: dropped packs are re-placed by fallback, and always NAMED.
    const nameOf = new Map(items.map((item) => [item.skuId, item.name || item.skuId]));
    const named = swap.report.dropped.map((skuId) => nameOf.get(skuId) ?? skuId);
    concessions.push({
      kind: "dropped",
      detail:
        `${swap.report.dropped.length} product(s) had no legal shelf in their band ` +
        "(the spec's own concession); the studio re-places them by fallback: " +
        `${named.slice(0, 5).join(", ")}${named.length > 5 ? ` and ${named.length - 5} more` : ""}`,
    });
  }
  for (const warning of swap.report.warnings) {
    concessions.push({ kind: "audit", detail: warning });
  }
  // The as-built carry leaks fill: pin the draft to its own regeneration and grow-to-close.
  const carried: StudioDraft = {
    ...remeasureDraft(items, swap.layout, runs, draft),
    poolingOverrides: swap.poolingOverrides,
  };
  const closed = closeFillGap(items, runs, carried, rules.fillFloorShare);
  const next = closed.draft;
  const after = closed.layout;
  concessions.push(...shedConcessionOf(after, displayed));
  concessions.push(...redistributionConcessionOf(displayed, after, items));
  const audit = auditMove({
    items,
    runs,
    before: { layout: displayed, draft },
    after: { layout: after, draft: next },
    roundTrip: { reproduced: swap.layout, floorExact: floorExactOf(items.length, rules) },
    fill: { floorShare: rules.fillFloorShare, dropTolerancePts: rules.fillDropTolerancePts },
  });
  if (!audit.pass) {
    concessions.push({
      kind: "audit",
      detail:
        `the rebuilt plan carries ${audit.failures.length} audit finding(s) ` +
        `(${[...new Set(audit.failures.map((failure) => `#${failure.check}`))].join(" ")}) — ` +
        "an orientation change re-derives every shelf; the un-swapped plan is one undo away",
    });
  }
  const aesthetics = {
    before: blockingScoreOf(items, displayed, runs, draft, aestheticsRules, verticalBlockShare),
    after: blockingScoreOf(items, after, runs, next, aestheticsRules, verticalBlockShare),
    contractChanged: true,
  };
  if (
    !Number.isNaN(aesthetics.before) &&
    !Number.isNaN(aesthetics.after) &&
    aesthetics.after < aesthetics.before
  ) {
    concessions.push({
      kind: "composite-drop",
      detail:
        `the swap costs aesthetics ${aesthetics.before} to ${aesthetics.after}; ` +
        "undo rejects it",
    });
  }
  return {
    draft: next,
    report: reportOf(moveClass, {
      surgical: false,
      audit,
      concessions,
      aesthetics,
      expectedSales: {
        before: expectedSalesOf(displayed, items),
        after: expectedSalesOf(after, items),
      },
      fill: fillPairOf({ layout: displayed }, { layout: after }, items, runs),
      mechanism: "rebuild",
      churn: churnOf(displayed, after),
    }),
  };
};

export const applyHierarchyMove = (
  items: readonly StudioItem[],
  runs: readonly StudioRunGeometry[],
  draft: StudioDraft,
  move: HierarchyMove,
  rules: HierarchyMoveRules = DEFAULT_HIERARCHY_MOVE_RULES,
  /** The plan the user is LOOKING AT, when it differs from `buildLayout(draft)`. It re-bases
   * only what the report SHOWS; the AUDIT keeps comparing regeneration to regeneration. */
  displayedBefore?: GeneratedLayout,
  /** How to read a layout as the CANVAS draws it. Without it churn falls back to the
   * contract basis, which counts movement in coordinates the renderer throws away. */
  project?: LayoutProjection,
  /** Merchant/category aesthetics used by every move gate and report. */
  aestheticsRules?: Partial<AestheticsRules>,
  /** Shelf-coverage share used by the move's exception score gates. */
  verticalBlockShare?: number,
): { draft: StudioDraft; report: MoveReport } => {
  const moveClass = classifyMove(draft, move);
  const rebase = (result: { draft: StudioDraft; report: MoveReport }) => {
    if (!result.report.committed) return result;
    if (!displayedBefore) {
      // No re-baselining asked for; only the churn BASIS is corrected.
      if (!project) return result;
      const before = buildLayout(items, draft, runs);
      const shipped = buildLayout(items, result.draft, runs);
      return {
        draft: result.draft,
        report: { ...result.report, churn: churnOf(before, shipped, project) },
      };
    }
    const shipped = buildLayout(items, result.draft, runs);
    const fill = fillPairOf({ layout: displayedBefore }, { layout: shipped }, items, runs);
    return {
      draft: result.draft,
      report: {
        ...result.report,
        fill,
        churn: churnOf(displayedBefore, shipped, project),
        aesthetics: result.report.aesthetics
          ? {
              ...result.report.aesthetics,
              before: blockingScoreOf(
                items,
                displayedBefore,
                runs,
                draft,
                aestheticsRules,
                verticalBlockShare,
              ),
            }
          : result.report.aesthetics,
        expectedSales: result.report.expectedSales
          ? {
              before: expectedSalesOf(displayedBefore, items),
              after: expectedSalesOf(shipped, items),
            }
          : result.report.expectedSales,
      },
    };
  };
  if (move.kind === "merge" || move.kind === "unmerge") {
    return rebase(
      applySwapMove(
        { items, runs, rules, aestheticsRules, verticalBlockShare },
        draft,
        move,
        moveClass,
      ),
    );
  }
  if (move.kind === "level") {
    return rebase(
      applyLevelMove(
        { items, runs, rules, aestheticsRules, verticalBlockShare },
        draft,
        move,
        moveClass,
      ),
    );
  }
  if (move.kind === "scoped-level") {
    return rebase(
      applyScopedLevelLift(
        { items, runs, rules, aestheticsRules, verticalBlockShare },
        draft,
        move,
        moveClass,
      ),
    );
  }
  if (
    (moveClass === "surgical-order" || moveClass === "band-transposition") &&
    items.length > 0 &&
    runs.length > 0
  ) {
    // Dispatch on the MEASURED shape of the moved level's values, not the declared class:
    // banded values re-tier in place, column values slide as whole blocks.
    const scope = resolveScope(items, draft, move.scopeKey, move.attr);
    if (scope) {
      const displayed = buildLayout(items, draft, runs);
      if (isBandedScope(scope.scopeItems, draft, displayed, move.attr)) {
        return rebase(
          applyRebandMove(
            { items, runs, rules, aestheticsRules, verticalBlockShare },
            draft,
            move,
            moveClass,
            displayed,
          ),
        );
      }
    }
    return rebase(
      applySurgicalValueMove(
        { items, runs, rules, aestheticsRules, verticalBlockShare },
        draft,
        move,
        moveClass,
      ),
    );
  }
  return rebase(applyValueMove(draft, move, moveClass));
};
